import {ChangeDetectionStrategy, Component, Signal, ViewEncapsulation, WritableSignal, computed, inject, linkedSignal, signal} from '@angular/core';
import {NgClass} from "@angular/common";
import {NavigationEnd, Router} from "@angular/router";
import {IconBadgeComponent} from "../../../shared/elements/icon-badge/icon-badge.component";
import {TranslatePipe} from "../../../shared/pipes/translate.pipe";
import {LinkComponent} from "../../../shared/elements/link/link.component";
import {RouterService} from "../../../shared/services/router.service";
import {FormButtonComponent} from "../../../shared/elements/form-button/form-button.component";
import {filter, map, startWith, take} from "rxjs";
import {LanguageService} from "../../../shared/services/language.service";
import {NotificationComponent} from "../../../shared/elements/notification/notification.component";
import {HeadlineComponent} from "../../../shared/elements/headline/headline.component";
import {DropDownListModule} from "@syncfusion/ej2-angular-dropdowns";
import {DialogModule} from "@syncfusion/ej2-angular-popups";
import {DividerDateComponent} from "../../../shared/elements/divider-date/divider-date.component";
import {FormDropdownComponent} from "../../../shared/elements/form-dropdown/form-dropdown.component";
import {FormRadioComponent} from "../../../shared/elements/form-radio/form-radio.component";
import {FormBuilder, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {DialogProfileLanguageComponent} from "../../profile/dialog-profile-language/dialog-profile-language.component";
import {DialogProfilePushComponent} from "../../profile/dialog-profile-push/dialog-profile-push.component";
import {TooltipComponent} from "../../../shared/elements/tooltip/tooltip.component";
import {AvatarComponent} from "../../../shared/elements/avatar/avatar.component";
import {LayoutService} from "../../../shared/services/layout.service";
import {CustomerService} from "../../../shared/services/customer.service";
import {ProfileService} from '../../../shared/services/profile.service';
import {WarningComponent} from "../../../shared/elements/warning/warning.component";
import {FirebaseService} from '../../../shared/services/firebase.service';
import {NotificationChannel} from '../../../shared/types/generated/notificationChannel';
import {NotificationResource} from '../../../shared/types/generated/notificationResource';
import {NotificationReadState} from '../../../shared/types/generated/notificationReadState';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {NavService} from '../../../shared/services/nav.service';
import {NotificationStateService} from '../../../shared/services/notification-state.service';
import {AppDownloadDialogComponent} from "../app-download-dialog/app-download-dialog.component";
import {AssistanceDialogComponent} from "../assistance-dialog/assistance-dialog.component";
import {IconBarPolicy, IconBarService} from '../../../shared/services/icon-bar.service';
import {ApiService} from '../../../shared/services/api.service';
import {CapabilitiesService} from '../../../shared/services/capabilities.service';
import {TokenService} from '../../../shared/services/token.service';
import {AppStoreResetService} from '../../../shared/services/app-store-reset.service';
import {DialogHomeIconsComponent} from "../../home/dialog-home-icons/dialog-home-icons.component";
import {EnvironmentService} from '../../../shared/services/environment.service';
import {ApiDomainOption} from '../../../shared/utils/runtime-api-url';
import {SettingsMenuMode} from '../../../shared/types/generated/settingsMenuMode';
import {environment} from '../../../../environments/environment';

interface UserMenuModeOption {
  id: SettingsMenuMode;
  name: string;
}
type UserMenuModeSource = 'default' | 'own';

@Component({
    selector: 'app-bar-top',
    imports: [
    IconBadgeComponent,
    TranslatePipe,
    LinkComponent,
    FormButtonComponent,
    NotificationComponent,
    HeadlineComponent,
    DropDownListModule,
    DialogModule,
    DividerDateComponent,
    FormDropdownComponent,
    FormRadioComponent,
    DialogProfileLanguageComponent,
    DialogProfilePushComponent,
    TooltipComponent,
    AvatarComponent,
    NgClass,
    WarningComponent,
    AppDownloadDialogComponent,
    DialogHomeIconsComponent,
    AssistanceDialogComponent,
    ReactiveFormsModule
],
    templateUrl: './bar-top.component.html',
    styleUrl: './bar-top.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BarTopComponent {
  public barIconService: IconBarService = inject(IconBarService);
  public routerService: RouterService = inject(RouterService);
  public layoutService: LayoutService = inject(LayoutService);
  public profileService: ProfileService = inject(ProfileService);
  public firebaseService: FirebaseService = inject(FirebaseService);
  public capabilities: CapabilitiesService = inject(CapabilitiesService);
  public customerService: CustomerService = inject(CustomerService);
  private notificationStateService: NotificationStateService = inject(NotificationStateService);
  private languageService: LanguageService = inject(LanguageService);
  private formBuilder: FormBuilder = inject(FormBuilder);
  private apiService: ApiService = inject(ApiService);
  private navService: NavService = inject(NavService);
  private tokenService: TokenService = inject(TokenService);
  private appStoreResetService: AppStoreResetService = inject(AppStoreResetService);
  private environmentService: EnvironmentService = inject(EnvironmentService);
  private router: Router = inject(Router);
  private currentRouteUrl: Signal<string> = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url)
    ),
    {initialValue: this.router.url}
  );


  public tab: 'notifications' | 'chat' | null = null;
  public status: NotificationReadState = NotificationReadState.UNREAD;
  public module: 'calendar' | 'channel' | 'chat' | 'intranet' | 'mapp' | 'meeting' | 'survey' | 'workflow' | null = null;
  public profileLanguageDialog: boolean = false;
  public profilePushDialog: boolean = false;
  public appDownloadDialog: boolean = false;
  public assistanceDialog: boolean = false;
  public notifications: Signal<NotificationResource[]> = this.notificationStateService.barTopNotifications;
  public iconBarPolicy: IconBarPolicy = IconBarPolicy.Toggleable;
  public currentBreakpoint: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' = 'xs';
  public homeIconsDialog: WritableSignal<boolean> = signal<boolean>(false);
  public userMenuModeDialog: WritableSignal<boolean> = signal<boolean>(false);
  public userMenuModeLoading: WritableSignal<boolean> = signal<boolean>(false);
  public userMenuModeSaving: WritableSignal<boolean> = signal<boolean>(false);
  public userMenuModeUsesOwnSetting: WritableSignal<boolean> = signal<boolean>(false);
  public apiDomainOptions: ApiDomainOption[] = this.environmentService.getConfiguredApiDomains();
  public domainSwitchingForAll: boolean = environment.api.domainSwitchingForAll;
  public apiUrlForm: FormGroup = this.formBuilder.nonNullable.group({apiUrl: this.environmentService.getApiUrl()});
  public userMenuModeForm: FormGroup = this.formBuilder.nonNullable.group({
    modeSource: 'default' as UserMenuModeSource,
    displayMode: SettingsMenuMode.SIDEBAR_ICONS_AND_TEXT
  });
  public userMenuModeOptions: Signal<UserMenuModeOption[]> =
    computed<UserMenuModeOption[]>(() => {
      this.languageService.language$();
      this.languageService.loadedLanguagesVersion$();

      return [
        {
          id: SettingsMenuMode.NO_SIDEBAR,
          name: this.languageService.getTranslationByKey('SETTINGSMENUDEFAULTDISPLAYMODENOSIDEBAR')
        },
        {
          id: SettingsMenuMode.SIDEBAR_ONLY_ICONS,
          name: this.languageService.getTranslationByKey('SETTINGSMENUDEFAULTDISPLAYMODESIDEBARONLYICONS')
        },
        {
          id: SettingsMenuMode.SIDEBAR_ICONS_AND_TEXT,
          name: this.languageService.getTranslationByKey('SETTINGSMENUDEFAULTDISPLAYMODESIDEBARICONSANDTEXT')
        },
      ];
    });


  public activeChannels: Signal<{id: string, name: string}[]> = linkedSignal(() => {
    const mappedModules = [
      {id: 'all', name: this.languageService.getTranslationByKey('ALLNOTIFICATIONS')},
      {id: NotificationChannel.USER, name: this.languageService.getTranslationByKey('USER')}
    ];
    const menuNamesByType = new Map<string, string>(
      this.navService.userMenu().map((menu) => [menu.type, menu.name])
    );
    const badgeChannelKeys = this.getBadgeChannelKeysExcludingChat();
    badgeChannelKeys.forEach((channel) => {
      if (channel === NotificationChannel.USER) {
        return;
      }

      mappedModules.push({
        id: channel,
        name: menuNamesByType.get(channel) ?? this.languageService.getTranslationByKey(channel.toUpperCase())
      });
    });

    return mappedModules;
  });

  public filterForm: FormGroup = this.formBuilder.group({
    module: ['all']
  });

  public constructor() {
    this.layoutService.scrolledToEnd
    .pipe(takeUntilDestroyed())
    .subscribe({
      next: (scrolledToEnd: boolean | string) => {
        if (scrolledToEnd && scrolledToEnd === 'bar-top') {
          this.loadList();
        }
      }
    })

    this.filterForm.get('module')?.valueChanges
    .pipe(takeUntilDestroyed())
    .subscribe(() => {
      this.notificationStateService.clearBarTopNotifications();
      this.loadList();
    });

    this.userMenuModeForm.get('modeSource')?.valueChanges
    .pipe(takeUntilDestroyed())
    .subscribe((modeSource: UserMenuModeSource) => {
      this.userMenuModeUsesOwnSetting.set(modeSource === 'own');
    });

    this.barIconService.iconBarPolicy
    .pipe(takeUntilDestroyed())
    .subscribe((policy) => {
      this.iconBarPolicy = policy;
    });

    this.router.events
    .pipe(takeUntilDestroyed())
    .subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.ensureIconBarToggleableOutsideHome(event.urlAfterRedirects);
        if (this.tab !== null) {
          this.closeTab();
        }
      }
    });

    this.ensureIconBarToggleableOutsideHome(this.router.url);

    this.layoutService.breakpointWidthType
    .pipe(takeUntilDestroyed())
    .subscribe((breakpoint) => {
      this.currentBreakpoint = breakpoint;
    });

    this.userMenuModeUsesOwnSetting.set(this.userMenuModeForm.get('modeSource')?.value === 'own');
  }

  public signOut(): void {
    this.apiService.logoutUser()
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.tokenService.clearToken();
          this.profileService.resetProfile();
          this.appStoreResetService.resetAllStores();
          const targetRoute = this.customerService.hasSsoOptions() ? '/auth/sso' : '/auth/login';
          this.routerService.navigateTo(targetRoute);
        },
        error: () => {
          this.tokenService.clearToken();
          this.profileService.resetProfile();
          this.appStoreResetService.resetAllStores();
          const targetRoute = this.customerService.hasSsoOptions() ? '/auth/sso' : '/auth/login';
          this.routerService.navigateTo(targetRoute);
        }
      });
  }

  public switchApiUrl(value: unknown): void {
    const selectedUrl = typeof value === 'string' ? value : null;
    if (!selectedUrl || selectedUrl === this.environmentService.getApiUrl()) {
      return;
    }

    this.environmentService.setApiUrlOverride(selectedUrl);
    this.tokenService.clearToken();
    this.profileService.resetProfile();
    this.appStoreResetService.resetAllStores();
    window.location.reload();
  }

  public setTab(tab: 'notifications' | 'chat'): void {
    this.tab = (tab === this.tab ? null : tab);
    this.status = NotificationReadState.UNREAD;
    this.module = (this.tab === 'chat' ? 'chat' : null);
    if (this.tab) {
      this.notificationStateService.clearBarTopNotifications();
      this.loadList();
    }
  }

  public closeTab(): void {
    this.tab = null;
  }

  public setStatusRead(): void {
    this.setStatus(NotificationReadState.READ);
  }
  
  public setStatusUnread(): void {
    this.setStatus(NotificationReadState.UNREAD);
  }

  private setStatus(status: NotificationReadState): void {
    this.status = status;
    this.notificationStateService.clearBarTopNotifications();
    this.loadList();
  }

  public hasNonChatBadgeAlert(): boolean {
    return this.notificationStateService.hasNonChatBadgeAlert();
  }

  public get iconsBadges(): Map<string, boolean> {
    return this.notificationStateService.iconsBadges();
  }

  public loadList(): void {
    if (!this.tab) {
      return;
    }

    const channels: NotificationChannel[] = []
    if(this.tab === 'notifications') {
      if(this.filterForm.get('module')?.value === 'all') {
        channels.push(...this.getChannelsForAllNotificationsSelection());
      } else {
        channels.push((this.filterForm.get('module')?.value) as NotificationChannel);
      }
    } else {
      channels.push(NotificationChannel.CHAT);
    }

    this.notificationStateService.loadBarTopNotifications(
      this.status,
      channels,
      true,
      25,
      (err) => {
        console.log(err);
      }
    );
  }

  public shouldShowNoNotificationsMessage(): boolean {
    if (!this.tab || this.notifications().length > 0) {
      return false;
    }

    if (this.tab === 'chat') {
      return !(this.iconsBadges.get(NotificationChannel.CHAT) ?? false);
    }

    const selectedModule = this.filterForm.get('module')?.value;
    if (selectedModule === 'all') {
      return !this.hasNonChatBadgeAlert();
    }

    if (typeof selectedModule !== 'string') {
      return true;
    }

    return !(this.iconsBadges.get(selectedModule) ?? false);
  }

  public toggleAutomaticTranslation(): void {
    this.profileService.setProfileAutoTranslation(!this.profileService.profile()?.user.auto_translation);
  }

  public isHomeRoute: Signal<boolean> = computed(() => this.currentRouteUrl().startsWith('/home'));

  public openUserMenuModeDialog(): void {
    this.userMenuModeDialog.set(true);
    this.userMenuModeLoading.set(true);

    this.apiService.getUserMenuMode()
    .pipe(take(1))
    .subscribe({
      next: (resource) => {
        const displayMode = resource.display_mode as SettingsMenuMode | null;
        this.userMenuModeForm.patchValue({
          modeSource: displayMode === null ? 'default' : 'own',
          displayMode: displayMode ?? SettingsMenuMode.SIDEBAR_ICONS_AND_TEXT
        });
      },
      error: (err) => {
        console.error('Error loading user menu mode', err);
        this.userMenuModeLoading.set(false);
      },
      complete: () => {
        this.userMenuModeLoading.set(false);
      }
    });
  }

  public closeUserMenuModeDialog(): void {
    this.userMenuModeDialog.set(false);
    this.userMenuModeLoading.set(false);
    this.userMenuModeSaving.set(false);
  }

  public saveUserMenuMode(): void {
    const useOwnSetting = this.userMenuModeForm.get('modeSource')?.value === 'own';
    const displayMode = useOwnSetting
      ? this.userMenuModeForm.get('displayMode')?.value as SettingsMenuMode
      : null;

    this.userMenuModeSaving.set(true);
    this.apiService.setUserMenuMode({display_mode: displayMode})
    .pipe(take(1))
    .subscribe({
      next: () => {
        this.customerService.reloadCustomerData();
        this.navService.reloadMenus(true);
        this.closeUserMenuModeDialog();
      },
      error: (err) => {
        console.error('Error saving user menu mode', err);
        this.userMenuModeSaving.set(false);
      }
    });
  }

  public markNotificationRead(notificationId: number): void {
    this.notificationStateService.markNotificationRead(
      notificationId,
      (err) => {
        console.error(err);
      }
    );
  }

  private ensureIconBarToggleableOutsideHome(url: string): void {
    if (!url.startsWith('/home')) {
      this.barIconService.setIconBarPolicy(IconBarPolicy.Toggleable);
    }
  }

  public markNotificationUnread(notificationId: number): void {
    this.notificationStateService.markNotificationUnread(
      notificationId,
      (err) => {
        console.error(err);
      }
    );
  }

  public setRead(notificationTab: 'notifications' | 'chat'): void {
    const channelsToSetAsRead: NotificationChannel[] = [];
    if (notificationTab === 'chat') {
      channelsToSetAsRead.push(NotificationChannel.CHAT);
    } else {
      if(this.filterForm.get('module')?.value === 'all') {
        channelsToSetAsRead.push(...this.getChannelsForAllNotificationsSelection());
      } else {
        channelsToSetAsRead.push((this.filterForm.get('module')?.value) as NotificationChannel);
      }
    }

    this.notificationStateService.setAllReadForChannels(
      channelsToSetAsRead,
      () => {
        this.notificationStateService.clearBarTopNotifications();
        this.loadList();
      },
      (err) => {
        console.error(err);
      }
    );
  }

  public setUnread(notificationTab: 'notifications' | 'chat'): void {
    if (notificationTab !== 'notifications') {
      return;
    }

    const notificationIds = this.notifications().map((notification) => notification.id);
    this.notificationStateService.setAllUnreadByIds(
      notificationIds,
      () => {
        this.notificationStateService.clearBarTopNotifications();
        this.loadList();
      },
      (err) => {
        console.error(err);
      }
    );
  }

  public shouldShowNotificationDateDivider(index: number): boolean {
    if (index === 0) {
      return true;
    }

    const items = this.notifications();
    const current = items[index];
    const previous = items[index - 1];

    if (!current || !previous) {
      return false;
    }

    const currentDate = new Date(current.created_at);
    const previousDate = new Date(previous.created_at);

    return (
      currentDate.getFullYear() !== previousDate.getFullYear() ||
      currentDate.getMonth() !== previousDate.getMonth() ||
      currentDate.getDate() !== previousDate.getDate()
    );
  }

  public formatDividerDate(item: NotificationResource): Date {
    return new Date(item.created_at);
  }

  private getBadgeChannelKeysExcludingChat(): string[] {
    const channelKeys = Array.from(this.iconsBadges.keys())
      .filter((key) => key !== NotificationChannel.CHAT);
    return Array.from(new Set(channelKeys));
  }

  public openPlatformInfo(): void {
    this.routerService.navigateTo('/platform-info');
  }

  public openAssist(): void {
    window.open('https://assist.humanstars.app', '_blank');
  }

  private getChannelsForAllNotificationsSelection(): NotificationChannel[] {
    const badgeChannels = this.getBadgeChannelKeysExcludingChat();
    const channels: string[] = [];

    if (badgeChannels.includes(NotificationChannel.USER)) {
      channels.push(NotificationChannel.USER);
    }

    channels.push(...badgeChannels.filter((channel) => channel !== NotificationChannel.USER));
    return channels as NotificationChannel[];
  }
}
